'use client';

import { useCallback, useState, useEffect } from 'react';

import NotificationIcon from '@/assets/icons/account/notification.svg';
import { Button } from '@/components/ui/button';
import { useNotificationCounts } from '@/hooks/useNotificationCounts';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { cn } from '@/lib/utils/cn';
import { useUpdateGlobalData } from '@/providers/GlobalDataProvider';

interface I_HeaderNotificationButtonProps {
  type?: 'default' | 'reward';
}

export function HeaderNotificationButton({
  type
}: I_HeaderNotificationButtonProps) {
  const [isMounted, setIsMounted] = useState(false);
  const isReward = type === 'reward';
  const { isFullyLoggedIn, openAccount } = useWalletConnection();
  const { total } = useNotificationCounts();
  const updateGlobalData = useUpdateGlobalData();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleNotificationClick = useCallback(() => {
    if (isReward) return;

    updateGlobalData({ accountSheetInitialView: 'notifications' });
    openAccount();
  }, [isReward, updateGlobalData, openAccount]);

  // Don't render anything during SSR or if conditions aren't met
  if (!isMounted || isReward || !isFullyLoggedIn) {
    return null;
  }

  return (
    <Button
      variant="secondary"
      onClick={handleNotificationClick}
      className={cn(
        'flex bg-transparent items-center justify-center w-9 h-9 rounded-full cursor-pointer transition-colors p-0 relative',
        'hover:bg-unset hover:border-border hover:border-1'
      )}
      aria-label="Notifications"
    >
      <NotificationIcon className="h-6 w-6 text-foreground" />
      {total > 0 && (
        <span
          className={cn(
            'absolute -top-1 -right-1 flex items-center justify-center',
            'min-w-5 h-5 px-1.5 rounded-full',
            'bg-primary text-primary-foreground text-xs font-bold',
            'leading-none'
          )}
        >
          {total > 99 ? '99+' : total}
        </span>
      )}
    </Button>
  );
}
